import { Warning } from "@phosphor-icons/react";
import { Banner, Tab, TabList, TabPanel, TabProvider } from "@replicate/ui";
import { FormProvider, useFormContext } from "react-hook-form";
import { P, match } from "ts-pattern";
import { useQueryParam } from "../../hooks";
import { getTrainingInputSchema } from "../../schema";
import type { AccessToken, CogInputSchema, Version } from "../../types";
import { usePlaygroundContext } from "../api-playground/context";
import { FeaturedInputs } from "../api-playground/featured-inputs";
import { InputForm } from "../api-playground/input-form";
import { InputFormFooter } from "../api-playground/input-form-footer";
import { RunWithContext } from "../api-playground/run-with";
import {
  cleanInputForSubmission,
  processInputForSubmission,
} from "../api-playground/util";
import { DestinationPreview } from "./destination-preview";
import { TrainWith } from "./docs";
import { ModelPicker } from "./model-picker";
import {
  parseCreateTrainingError,
  useCreateVersionTrainingMutation,
  type TrainingAction,
} from "./shared";

interface TrainingFormProps {
  authenticated: boolean;
  owner: string;
  avatar: string;
  token: AccessToken | null;
  version: Version;
  destinations?: { name: string; username: string }[];
  action: TrainingAction;
  trainingVersion?: Version;
  properties: CogInputSchema["properties"];
  showTabs?: boolean;
}

const mutationFallbackErrorMessages = {
  create: "Failed to create training",
  update: "Failed to update training",
};

function getProbableTrainableInputs(properties): string[] {
  if (!properties) {
    return [];
  }

  const possibleTrainableInputs = Object.keys(properties).filter((key) => {
    return (
      properties[key].type === "string" && properties[key].format === "uri"
    );
  });

  return match(possibleTrainableInputs)
    .with([], () => [])
    .with([P.string], (value) => value)
    .with(P.array(P.string), (value) =>
      value.filter((input) => input.toLowerCase().includes("input"))
    )
    .exhaustive();
}

function TrainingFormInner({
  action,
  authenticated,
  owner,
  avatar,
  destinations,
  properties,
  trainingVersion,
  mutation,
  parsedMutationError,
  onSubmit,
  form,
  probableRequiredInputs,
  probableTrainingInputs,
}: TrainingFormProps & {
  mutation: any;
  parsedMutationError: any;
  onSubmit: any;
  form: any;
  probableRequiredInputs: string[];
  probableTrainingInputs: string[];
}) {
  const {
    modelInputSettings,
    permissions,
    usesVersionlessApi,
    version,
    model,
  } = usePlaygroundContext();
  const canEditFeaturedInputs = permissions.edit_featured_inputs;
  const inputSchema = usesVersionlessApi
    ? null
    : getTrainingInputSchema(version._extras);

  return (
    <>
      <div className="pb-4">
        {canEditFeaturedInputs && inputSchema && model ? (
          <div className="flex gap-2 items-center justify-end">
            <FeaturedInputs
              type="training"
              schema={inputSchema}
              alwaysRequired={probableRequiredInputs}
              model={model}
            />
          </div>
        ) : null}
        {parsedMutationError && (
          <div className="mb-4">
            <Banner
              icon={<Warning />}
              severity="error"
              description={parsedMutationError.message}
            />
          </div>
        )}
        <InputForm
          hiddenFields={modelInputSettings.hidden}
          disabled={!authenticated}
          hideAdvancedInputs={false}
          hideInitialFilePreview
          itemClass="py-4"
          onSubmit={onSubmit}
          properties={properties}
          required={probableRequiredInputs}
          unstable_getExtraFieldProperties={(name) => {
            if (!probableTrainingInputs.includes(name)) {
              return {};
            }

            return {
              placeholder:
                "Upload a compatible file (e.g. a zip) containing your training data",
            };
          }}
        >
          {action === "create" && (
            <ModelPicker
              disabled={!authenticated}
              models={destinations ?? []}
              owner={owner}
              avatar={avatar}
              setFocus={form.setFocus}
            />
          )}
          {action === "update" && trainingVersion && (
            <DestinationPreview
              owner={owner}
              avatar={avatar}
              version={trainingVersion}
            />
          )}
        </InputForm>
      </div>
      <InputFormFooter
        disabled={!authenticated}
        isAuthenticated={authenticated}
        isLoading={mutation.isPending}
        onReset={form.reset}
        submitLabel="Create training"
      />
    </>
  );
}

export default function TrainingForm({
  action,
  authenticated,
  owner,
  avatar,
  token,
  destinations = [],
  version,
  trainingVersion,
  properties,
  showTabs = true,
}: TrainingFormProps) {
  const form = useFormContext();

  const formValues = form.watch();

  const mutation = useCreateVersionTrainingMutation({
    errorMessage: mutationFallbackErrorMessages[action],
  });

  const parsedMutationError = parseCreateTrainingError(
    mutation.error,
    mutationFallbackErrorMessages[action]
  );

  const probableTrainingInputs = getProbableTrainableInputs(properties);
  const probableRequiredInputs = [
    "destination",
    ...probableTrainingInputs,
    ...(getTrainingInputSchema(version._extras)?.required ?? []),
  ];

  const onSubmit = (input: Record<string, any>) => {
    const { destination, visibility, createModel, ...rest } = input;

    const cleanInput = cleanInputForSubmission(rest, properties);
    const processedInput = processInputForSubmission(cleanInput, properties);

    mutation.mutate({
      destination,
      visibility,
      input: processedInput,
      inputSchema: getTrainingInputSchema(version._extras) ?? null,
      version,
    });
  };

  if (!showTabs) {
    return (
      <TrainingFormInner
        action={action}
        authenticated={authenticated}
        owner={owner}
        avatar={avatar}
        destinations={destinations}
        properties={properties}
        trainingVersion={trainingVersion}
        mutation={mutation}
        parsedMutationError={parsedMutationError}
        onSubmit={onSubmit}
        form={form}
        token={token}
        version={version}
        probableRequiredInputs={probableRequiredInputs}
        probableTrainingInputs={probableTrainingInputs}
      />
    );
  }

  const DEFAULT_TAB = "form";
  const [activeTabId, setActiveTabId] = useQueryParam("input", DEFAULT_TAB);

  return (
    <TabProvider
      defaultActiveId={DEFAULT_TAB}
      activeId={activeTabId}
      setActiveId={(id) => setActiveTabId(id ?? undefined)}
      selectedId={activeTabId ?? DEFAULT_TAB}
    >
      <TabList className="mb-2" responsive size="sm">
        <Tab id="form">Form</Tab>
        <Tab id="python">Python</Tab>
        <Tab id="nodejs">Node.js</Tab>
      </TabList>
      <FormProvider {...form}>
        <TabPanel>
          <TrainingFormInner
            action={action}
            authenticated={authenticated}
            owner={owner}
            avatar={avatar}
            destinations={destinations}
            properties={properties}
            trainingVersion={trainingVersion}
            mutation={mutation}
            parsedMutationError={parsedMutationError}
            onSubmit={onSubmit}
            form={form}
            token={token}
            version={version}
            probableRequiredInputs={probableRequiredInputs}
            probableTrainingInputs={probableTrainingInputs}
          />
        </TabPanel>
        <TabPanel>
          <TrainWith
            context={RunWithContext.Python}
            input={formValues}
            token={token}
            version={version}
            schema={properties}
            action={action}
            owner={owner}
            requiredInputs={probableRequiredInputs}
          />
        </TabPanel>
        <TabPanel>
          <TrainWith
            context={RunWithContext.NodeJS}
            input={formValues}
            token={token}
            version={version}
            schema={properties}
            action={action}
            owner={owner}
            requiredInputs={probableRequiredInputs}
          />
        </TabPanel>
      </FormProvider>
    </TabProvider>
  );
}
